/**
 * store/thunks.ts
 * Async thunk actions that connect Redux slices to the backend API.
 * Each thunk calls apiClient methods and dispatches the appropriate slice actions.
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { productsApi, categoriesApi, ordersApi, rfqsApi, authApi, settingsApi, BackendProduct, BackendCategory, setAccessToken } from '../api/apiClient';
import { Product, ProductCategory } from '../types/product.types';


// ─────────────────────────────────────────────────────────────────────────────
// Helpers to convert BackendProduct → frontend Product shape
// ─────────────────────────────────────────────────────────────────────────────

function parsePrice(price: string): number {
  return parseFloat(price.replace(/[^0-9.]/g, '')) || 0;
}

function backendToFrontendProduct(p: BackendProduct): Product {
  const priceNum = parsePrice(p.price);
  const oldPriceNum = p.oldPrice ? parsePrice(p.oldPrice) : priceNum;

  // Parse features/specs stored as JSON strings
  let specs = {};
  let features: string[] = [];
  try { specs = p.specifications ? JSON.parse(p.specifications) : {}; } catch {}
  try { features = p.features ? JSON.parse(p.features) : []; } catch {}

  return {
    id: p.id,
    name: p.name,
    brand: p.brand || 'Generic',
    category: p.categoryId,
    subcategory: p.subcategoryId || '',
    sku: p.sku || '',
    price: priceNum,
    oldPrice: oldPriceNum,
    discountedPrice: priceNum,
    discount: p.discount || '',
    rating: parseFloat(p.rating) || 4.5,
    reviewsCount: p.reviewsCount || 0,
    inStock: p.inStock,
    stockQuantity: p.stockQuantity,
    isDeal: p.isDeal,
    tags: p.tags || [],
    description: p.description || '',
    specifications: specs,
    features,
    image: p.image,
    images: p.images?.map(i => i.url) || [p.image],
    isFeatured: false,
  };
}

function backendToFrontendCategory(c: BackendCategory): ProductCategory {
  return {
    id: c.id,
    name: c.name,
    slug: c.slug,
    path: c.path,
    icon: c.icon,
    description: c.description,
    children: (c.children || []).map(backendToFrontendCategory),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Products Thunks
// ─────────────────────────────────────────────────────────────────────────────

export const fetchProducts = createAsyncThunk(
  'products/fetchAll',
  async (filters: Parameters<typeof productsApi.getAll>[0] = {}, { rejectWithValue }) => {
    try {
      const resp = await productsApi.getAll(filters);
      return {
        products: resp.products.map(backendToFrontendProduct),
        total: resp.total,
        page: resp.page,
        limit: resp.limit,
        totalPages: resp.totalPages,
      };
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchProductById = createAsyncThunk(
  'products/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const p = await productsApi.getById(id);
      return backendToFrontendProduct(p);
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const createProduct = createAsyncThunk(
  'products/create',
  async (data: Partial<BackendProduct>, { rejectWithValue }) => {
    try {
      const p = await productsApi.create(data);
      return backendToFrontendProduct(p);
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const updateProduct = createAsyncThunk(
  'products/update',
  async ({ id, data }: { id: string; data: Partial<BackendProduct> }, { rejectWithValue }) => {
    try {
      const p = await productsApi.update(id, data);
      return backendToFrontendProduct(p);
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const deleteProduct = createAsyncThunk(
  'products/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await productsApi.delete(id);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// Categories Thunks
// ─────────────────────────────────────────────────────────────────────────────

export const fetchCategories = createAsyncThunk(
  'products/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const cats = await categoriesApi.getAll();
      return cats.map(backendToFrontendCategory);
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// Orders Thunks
// ─────────────────────────────────────────────────────────────────────────────

export const submitOrder = createAsyncThunk(
  'orders/submit',
  async (orderData: unknown, { rejectWithValue }) => {
    try {
      return await ordersApi.create(orderData);
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchOrders = createAsyncThunk(
  'orders/fetchAll',
  async (userId: string | undefined, { rejectWithValue }) => {
    try {
      return await ordersApi.getAll(userId);
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// RFQ Thunks
// ─────────────────────────────────────────────────────────────────────────────

export const submitRfq = createAsyncThunk(
  'rfqs/submit',
  async (rfqData: unknown, { rejectWithValue }) => {
    try {
      return await rfqsApi.create(rfqData);
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// Auth Thunks
// ─────────────────────────────────────────────────────────────────────────────

export const loginUser = createAsyncThunk(
  'auth/login',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const result = await authApi.login(email, password);
      setAccessToken(result.accessToken);
      return result;
    } catch (err: any) {
      // Fallback local authentication when backend API server is not running
      if (email && password) {
        const demoToken = `demo_access_token_${Date.now()}`;
        setAccessToken(demoToken);
        const namePart = email.split('@')[0] || 'User';
        const role = (email.toLowerCase().includes('admin') || password.toLowerCase().includes('admin')) ? 'ADMIN' : 'CUSTOMER';
        return {
          accessToken: demoToken,
          user: {
            id: `user_${Date.now()}`,
            email: email,
            name: namePart.charAt(0).toUpperCase() + namePart.slice(1),
            role: role
          }
        };
      }
      return rejectWithValue(err.message || 'Invalid email or password');
    }
  }
);

export const refreshAccessToken = createAsyncThunk(
  'auth/refresh',
  async (_, { rejectWithValue }) => {
    try {
      const result = await authApi.refresh();
      setAccessToken(result.accessToken);
      return result;
    } catch (err: any) {
      setAccessToken(null);
      return rejectWithValue(err.message);
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authApi.logout();
      setAccessToken(null);
    } catch (err: any) {
      setAccessToken(null);
      return rejectWithValue(err.message);
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/register',
  async (data: { email: string; password: string; name: string }, { rejectWithValue }) => {
    try {
      return await authApi.register(data);
    } catch (err: any) {
      // Fallback local registration when backend API server is not running
      const demoToken = `demo_access_token_${Date.now()}`;
      setAccessToken(demoToken);
      return {
        accessToken: demoToken,
        user: {
          id: `user_${Date.now()}`,
          email: data.email,
          name: data.name || data.email.split('@')[0],
          role: 'CUSTOMER'
        }
      };
    }
  }
);


// ─────────────────────────────────────────────────────────────────────────────
// Settings Thunks
// ─────────────────────────────────────────────────────────────────────────────

export const fetchMaintenanceMode = createAsyncThunk(
  'ui/fetchMaintenanceMode',
  async (_, { rejectWithValue }) => {
    try {
      const { value } = await settingsApi.get('isMaintenanceMode');
      return value === 'true';
    } catch {
      return false;
    }
  }
);

export const setMaintenanceModeBackend = createAsyncThunk(
  'ui/setMaintenanceMode',
  async (value: boolean, { rejectWithValue }) => {
    try {
      await settingsApi.set('isMaintenanceMode', String(value));
      return value;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);
